import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, message, Space, Popconfirm } from 'antd'; 
import { useNavigate } from 'react-router-dom';
import App from "../../Utils/LayoutMobile"; 
import dayjs from 'dayjs';

// ── Theme Constants ──
const C_PINK      = "#F04DA0";
const C_PURPLE    = "#8B2FC9";
const C_GRAD      = `linear-gradient(135deg, ${C_PINK}, ${C_PURPLE})`;
const C_SOFT_BG   = "#F8F4FF";
const C_TEXT_MAIN = "#1a0040";
const C_TEXT_MUTED= "#64748B";

// ── Icons ──
const UserIcon = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const MailIcon = () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
const TrashIcon = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>;
const PlusIcon = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const StarIcon = ({ filled }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "#fbbf24" : "none"} stroke={filled ? "#fbbf24" : "#cbd5e1"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

// ── Section Label ──
function SectionLabel({ children }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:C_PINK, whiteSpace:"nowrap" }}>
        {children}
      </span>
      <span style={{ flex:1, height:1.5, background:`linear-gradient(90deg, rgba(240,77,160,0.35), transparent)`, borderRadius:2 }}/>
    </div>
  );
}

const FeedbackList = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/v1/Feedback/feedback`);

      // Backend now returns { success: true, data: [...] }
      // Falls back gracefully if the shape ever changes
      const responseData = response.data?.data ?? response.data ?? [];
      const list = Array.isArray(responseData) ? responseData : [];

      setFeedbacks(list);
    } catch (err) {
      console.error("Fetch error:", err);
      message.error('Failed to load feedbacks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/v1/Feedback/feedback/${id}`);
      message.success('Feedback deleted successfully');
      fetchFeedbacks();
    } catch (err) {
      console.error(err);
      message.error('Failed to delete feedback');
    }
  };

  const columns = [
    {
      title: 'Reviewer Info',
      key: 'reviewer',
      width: 250,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
          <div className="avatar-wrap">
            <UserIcon />
          </div>
          <div>
            <div style={{ fontWeight: 800, color: C_TEXT_MAIN, fontSize: "14.5px", marginBottom: "4px" }}>
              {record.name || "Anonymous"}
            </div>
            <span style={{ display: "flex", alignItems: "center", gap: "6px", color: C_TEXT_MUTED, fontSize: "12.5px" }}>
              <span style={{ color: C_PURPLE }}><MailIcon /></span> {record.email || "No email"}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      width: 150,
      render: (rating) => {
        const ratingVal = Number(rating) || 5;
        return (
          <Space size={2}>
            {[1, 2, 3, 4, 5].map(star => (
              <StarIcon key={star} filled={star <= ratingVal} />
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Feedback Message',
      key: 'feedbackText',
      render: (_, record) => (
        <div className="message-box">
          {record.feedbackText
            ? record.feedbackText
            : <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>No message provided.</span>
          }
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date) => (
        <span style={{ color: C_TEXT_MAIN, fontSize: "13px", fontWeight: 600 }}>
          {date ? dayjs(date).format('MMM D, YYYY') : "Unknown"}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title={<span style={{ fontWeight: 700, color: C_TEXT_MAIN }}>Delete Feedback?</span>}
          description="Are you sure you want to permanently delete this review?"
          onConfirm={() => handleDelete(record._id)}
          okText="Yes, delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true, style: { borderRadius: "8px", fontWeight: 600 } }}
          cancelButtonProps={{ style: { borderRadius: "8px", fontWeight: 600 } }}
        >
          <button className="action-btn delete-btn" title="Delete Feedback">
            <TrashIcon />
          </button>
        </Popconfirm>
      ),
    },
  ];

  const sortedData = [...feedbacks].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <App>
      <div className="lc">
        <div className="mw">
          
          <div className="header-flex">
            <div>
              <SectionLabel>Participant Insights</SectionLabel>
              <h1 className="sec-title">Event <span style={{ color: C_PINK }}>Feedback</span></h1>
              <p className="sec-sub">Review testimonials and ratings submitted by attendees.</p>
            </div>

            <button className="btn-cta" onClick={() => navigate("/feedback/create")}>
              <PlusIcon /> Add Manual Feedback
            </button>
          </div>

          <div className="glass table-wrapper">
            <Table
              columns={columns}
              dataSource={sortedData}
              rowKey="_id"
              loading={loading}
              pagination={{ 
                pageSize: 10,
                position: ["bottomCenter"],
                showSizeChanger: true
              }}
              className="premium-table"
            />
          </div>

        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; }

        .lc { font-family: 'Segoe UI', sans-serif; background: ${C_SOFT_BG}; min-height: 100vh; padding: 40px 0 80px; }
        .mw { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

        .header-flex { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 40px; gap: 20px; flex-wrap: wrap; }
        .sec-title { font-size: clamp(28px, 4vw, 38px); font-weight: 800; color: ${C_TEXT_MAIN}; letter-spacing: -0.02em; line-height: 1.15; margin: 0 0 8px; margin-top: 0; }
        .sec-sub { font-size: 15px; color: ${C_TEXT_MUTED}; margin: 0; }

        .btn-cta { display: inline-flex; align-items: center; gap: 8px; padding: 14px 24px; background: ${C_GRAD}; border: none; border-radius: 50px; color: #fff; font-size: 14px; font-weight: 700; cursor: pointer; box-shadow: 0 10px 24px rgba(240,77,160,0.3); transition: all 0.25s; white-space: nowrap; }
        .btn-cta:hover { transform: translateY(-2px); box-shadow: 0 14px 28px rgba(240,77,160,0.4); }

        .avatar-wrap { width: 42px; height: 42px; border-radius: 12px; background: linear-gradient(135deg, rgba(139,47,201,0.1), rgba(240,77,160,0.1)); border: 1px solid rgba(139,47,201,0.15); color: ${C_PURPLE}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

        .message-box { background: #faf8fc; border: 1px solid rgba(139,47,201,0.08); padding: 12px 16px; border-radius: 10px; font-size: 13.5px; color: ${C_TEXT_MUTED}; line-height: 1.5; max-width: 500px; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }

        .action-btn { width: 36px; height: 36px; border-radius: 10px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; }
        .delete-btn { background: rgba(248, 113, 113, 0.1); color: #ef4444; }
        .delete-btn:hover { background: rgba(248, 113, 113, 0.2); transform: translateY(-2px); }

        .glass { background: #fff; border-radius: 20px; padding: 12px 24px 24px; border: 1px solid rgba(139,47,201,0.08); box-shadow: 0 10px 40px rgba(0,0,0,0.04); overflow: hidden; }

        .premium-table .ant-table { background: transparent !important; font-family: inherit; }
        .premium-table .ant-table-thead > tr > th { background: transparent !important; color: ${C_TEXT_MUTED}; font-weight: 700; font-size: 12.5px; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid rgba(139,47,201,0.08); padding: 20px 16px; }
        .premium-table .ant-table-thead > tr > th::before { display: none !important; }
        .premium-table .ant-table-tbody > tr > td { border-bottom: 1px solid rgba(139,47,201,0.05); padding: 20px 16px; transition: background 0.3s; vertical-align: top; }
        .premium-table .ant-table-tbody > tr:hover > td { background: #fcfaff !important; }
        .premium-table .ant-pagination { margin-top: 24px !important; }
        .premium-table .ant-pagination-item-active { border-color: ${C_PINK}; background: rgba(240,77,160,0.05); }
        .premium-table .ant-pagination-item-active a { color: ${C_PINK} !important; }

        @media(max-width: 768px) {
          .glass { padding: 12px; border-radius: 16px; }
          .header-flex { flex-direction: column; align-items: flex-start; gap: 16px; }
          .btn-cta { width: 100%; justify-content: center; }
        }
      `}</style>
    </App>
  );
};

export default FeedbackList;